import React, { useState, useEffect } from 'react';
import { db, auth } from "shared/utils/firebase";
import { collection, getDocs, query, where, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { Folder, Clock, ExternalLink, Edit2, Trash2, Check, X } from 'react-feather';
import styles from './codeProjects.module.css';

const CodeProjects = ({ onRestoreProject, onBackToEditor }) => {
    const [savedCodes, setSavedCodes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [renamingId, setRenamingId] = useState(null);
    const [renameDraft, setRenameDraft] = useState('');

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                fetchSavedCodes(user);
            } else {
                setSavedCodes([]);
                setIsLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    const fetchSavedCodes = async (user) => {
        if (!user) return;
        setIsLoading(true);
        console.log("Fetching saved code projects for user:", user.uid);

        try {
            // Primary query on codeProjects collection
            const q = query(
                collection(db, "codeProjects"),
                where("userId", "==", user.uid),
                orderBy("timestamp", "desc")
            );
            const querySnapshot = await getDocs(q);
            const docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log(`Found ${docs.length} code projects with ordered query.`);
            setSavedCodes(docs);
        } catch (error) {
            console.warn("Ordered fetch failed, trying fallback without sorting:", error.message);

            try {
                const qFallback = query(
                    collection(db, "codeProjects"),
                    where("userId", "==", user.uid)
                );
                const querySnapshot = await getDocs(qFallback);
                const docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                docs.sort((a, b) => {
                    const timeA = a.timestamp?.seconds || 0;
                    const timeB = b.timestamp?.seconds || 0;
                    return timeB - timeA;
                });

                console.log(`Found ${docs.length} code projects with fallback query.`);
                setSavedCodes(docs);
            } catch (fallbackError) {
                console.error("Both primary and fallback fetch failed:", fallbackError);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteProject = async (e, projectId) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to delete this project?")) return;

        try {
            await deleteDoc(doc(db, "codeProjects", projectId));
            setSavedCodes(prev => prev.filter(item => item.id !== projectId));
        } catch (error) {
            console.error("Delete failed:", error);
            alert("Failed to delete project.");
        }
    };

    const handleStartRename = (e, item) => {
        e.stopPropagation();
        setRenamingId(item.id);
        setRenameDraft(item.title || 'CodeBox untitled');
    };

    const handleRenameConfirm = async (e, projectId) => {
        e.stopPropagation();
        if (!renameDraft.trim()) return;

        try {
            await updateDoc(doc(db, "codeProjects", projectId), {
                title: renameDraft.trim()
            });
            setSavedCodes(prev => prev.map(item =>
                item.id === projectId ? { ...item, title: renameDraft.trim() } : item
            ));
            setRenamingId(null);
        } catch (error) {
            console.error("Rename failed:", error);
            alert("Failed to rename project.");
        }
    };

    const handleRenameCancel = (e) => {
        e.stopPropagation();
        setRenamingId(null);
        setRenameDraft('');
    };

    return (
        <div className={styles.galleryContainer}>
            <div className={styles.headerBar}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Folder size={24} color="#667eea" />
                    <h2 className={styles.galleryTitle}>My Code Projects</h2>
                </div>
                {onBackToEditor && (
                    <button className={styles.backBtn} onClick={onBackToEditor}>
                        Back to Editor
                    </button>
                )}
            </div>

            {isLoading && <div style={{ color: '#94a3b8', padding: '1rem' }}>Loading saved code projects...</div>}

            {!isLoading && savedCodes.length === 0 && (
                <div style={{ color: '#64748b', padding: '2rem 1rem', textAlign: 'center' }}>
                    No saved code projects found. Create and save code in CodeBox editor to see them here!
                </div>
            )}

            <div className={styles.galleryScroll}>
                {savedCodes.map((item) => (
                    <div key={item.id} className={styles.codeCard} onClick={() => onRestoreProject(item)}>
                        <div className={styles.cardHeader}>
                            <span className={styles.cardLang}>{item.primaryLanguage || (item.designFiles?.[0]?.language) || 'verilog'}</span>
                            <div className={styles.cardActions}>
                                <button className={styles.actionIcon} onClick={(e) => handleStartRename(e, item)} title="Rename">
                                    <Edit2 size={14} />
                                </button>
                                <button className={`${styles.actionIcon} ${styles.deleteIcon}`} onClick={(e) => handleDeleteProject(e, item.id)} title="Delete">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            {renamingId === item.id ? (
                                <div className={styles.renameContainer} onClick={(e) => e.stopPropagation()}>
                                    <input
                                        autoFocus
                                        className={styles.renameInput}
                                        value={renameDraft}
                                        onChange={(e) => setRenameDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleRenameConfirm(e, item.id);
                                            else if (e.key === 'Escape') handleRenameCancel(e);
                                        }}
                                    />
                                    <div className={styles.renameButtons}>
                                        <button className={styles.confirmBtn} onClick={(e) => handleRenameConfirm(e, item.id)}>
                                            <Check size={14} />
                                        </button>
                                        <button className={styles.cancelBtn} onClick={(e) => handleRenameCancel(e)}>
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <h4 className={styles.cardTitle}>{item.title || 'CodeBox untitled'}</h4>
                            )}
                            <p className={styles.cardDate}>
                                <Clock size={12} /> {item.timestamp && typeof item.timestamp.toDate === 'function' ? item.timestamp.toDate().toLocaleDateString() : 'Just now'}
                            </p>
                        </div>
                        <div className={styles.cardFooter}>
                            <span className={styles.cardFiles}>{(item.totalFiles || (item.designFiles?.length + item.tbFiles?.length)) || 0} files</span>
                            <button className={styles.openLabel}>
                                Open Editor <ExternalLink size={14} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CodeProjects;
